import { useEffect, useRef, useState } from 'react';
import { Menu, MenuItem, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '@workos-inc/authkit-react';

import { BRAND } from '../../shared/theme/paletteDark';
import { ACCOUNTS_ENABLED } from '../account/accountsEnabled';
import { ACCOUNT_PATHS } from '../account/paths';
import { arrivalFrom, type ReturnTo } from '../account/returnTo';
import CalendarSyncDialog from './CalendarSyncDialog';
import { DIALOG_BG } from './dialogParts';
import ShareLinkDialog from './ShareLinkDialog';

/**
 * Share My Schedule's menu (D13): a public link, a calendar that keeps
 * itself up to date, or a one-off .ics file.
 *
 * The first two live in an account. Logged out, they go to login first and
 * come back to My Schedule with the chosen dialog open.
 */

type Which = 'link' | 'calendar';

interface Props {
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Item 3: the existing export dialog, unchanged. */
  onDownload: () => void;
  /** Nothing on the schedule or saved: nothing to download. */
  downloadDisabled: boolean;
}

export default function ShareMenu({ anchor, onClose, onDownload, downloadDisabled }: Props) {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dialog, setDialog] = useState<Which | null>(null);
  const arrived = useRef(false);

  /**
   * Back from logging in to share: open what was asked for, once. The arrival
   * state is spent either way, so a reload doesn't bring it back.
   */
  useEffect(() => {
    if (arrived.current || isLoading) return;
    arrived.current = true;
    const arrival = arrivalFrom(location.state);
    if (!arrival) return;
    if (arrival.share && user) setDialog(arrival.share);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [isLoading, user, location, navigate]);

  const choose = (which: Which) => {
    onClose();
    if (user) {
      setDialog(which);
      return;
    }
    const returnTo: ReturnTo = { path: '/', arrival: { view: 'schedule', share: which } };
    navigate(ACCOUNT_PATHS.logIn, { state: { returnTo } });
  };

  // The brake hides the way into an account, not what an account already has.
  const accountItems = Boolean(user) || ACCOUNTS_ENABLED;
  const needsLogin = !user;

  const item = (label: string, hint: string) => (
    <>
      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 11.5, opacity: 0.6 }}>{hint}</Typography>
    </>
  );

  return (
    <>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={onClose}
        slotProps={{ paper: { sx: { background: DIALOG_BG, color: BRAND.parchment, minWidth: 260 } } }}
      >
        {accountItems && (
          <MenuItem onClick={() => choose('link')} sx={{ display: 'block', py: 1 }}>
            {item('Share a public link', needsLogin ? 'Log in or sign up first' : 'Anyone with the link can see your schedule')}
          </MenuItem>
        )}
        {accountItems && (
          <MenuItem onClick={() => choose('calendar')} sx={{ display: 'block', py: 1 }}>
            {item(
              'Sync with my personal calendar',
              needsLogin ? 'Log in or sign up first' : 'Google, Apple or Outlook, kept up to date',
            )}
          </MenuItem>
        )}
        <MenuItem
          disabled={downloadDisabled}
          onClick={() => {
            onClose();
            onDownload();
          }}
          sx={{ display: 'block', py: 1 }}
        >
          {item('Download an ICS', downloadDisabled ? 'Nothing on your schedule yet' : 'A one-off .ics file to import anywhere')}
        </MenuItem>
      </Menu>

      {user && <ShareLinkDialog open={dialog === 'link'} onClose={() => setDialog(null)} />}
      {user && <CalendarSyncDialog open={dialog === 'calendar'} onClose={() => setDialog(null)} />}
    </>
  );
}
